import type { PolicyRow } from "../api/admin";
import { t } from "../i18n";

/**
 * Policies as the screens read them: lifecycle rules written as sentences, and the message review
 * settings with their defaults. The shapes match the API's LifecyclePolicyConfig and
 * GatePolicyConfig; fields a screen does not show are kept as they were.
 */
export type LifecycleRule =
  | {
      when: "no_inbound_for";
      hours: number;
      statuses?: string[];
      onlyAfterOutbound?: boolean;
      /** `choose`: the AI picks the outcome, `close` when it cannot tell (decision 0044). */
      then: { close: string; note?: string; choose?: boolean };
    }
  | { when: "window_expired"; then: { status: "waiting_contact" } }
  | {
      when: "unanswered_new_for";
      hours: number;
      then: { priority: "high" | "urgent" };
    };

export const RULE_KINDS = [
  "no_inbound_for",
  "unanswered_new_for",
  "window_expired",
] as const;
export type RuleKind = (typeof RULE_KINDS)[number];

export function blankRule(
  when: RuleKind,
  closeWith = "no_response",
): LifecycleRule {
  if (when === "no_inbound_for")
    return { when, hours: 48, then: { close: closeWith, choose: true } };
  if (when === "unanswered_new_for")
    return { when, hours: 4, then: { priority: "high" } };
  return { when, then: { status: "waiting_contact" } };
}

/** Another kind of rule keeps the hours when both have them. */
export function changeRuleKind(
  rule: LifecycleRule,
  when: RuleKind,
  closeWith: string,
): LifecycleRule {
  const next = blankRule(when, closeWith);
  if ("hours" in rule && "hours" in next) next.hours = rule.hours;
  return next;
}

export function lifecycleRules(config: unknown): LifecycleRule[] {
  const rules = (config as { rules?: unknown[] } | null)?.rules;
  return Array.isArray(rules)
    ? (JSON.parse(JSON.stringify(rules)) as LifecycleRule[])
    : [];
}

export interface GateConfig {
  blockedTerms: string[];
  allowedLinkDomains: string[];
  piiPatterns: boolean;
  promisePatterns: string[];
  promisePhrases: string[];
  instructions: string;
  useAiJudge: boolean;
  overrideRoles: string[];
  shadowOf: "auto_fix" | "suggest" | "block";
  /** Whose messages it checks; people only unless the company adds more (decision 0032). */
  authors: GateAuthor[];
}

export const GATE_AUTHORS = ["member", "assistant", "flow"] as const;
export type GateAuthor = (typeof GATE_AUTHORS)[number];

export const GATE_ROLES = [
  "owner",
  "admin",
  "manager",
  "supervisor",
  "operator",
] as const;
export const GATE_MODES = ["log", "suggest", "auto_fix", "block"] as const;

export function gateConfig(config: unknown): GateConfig {
  const c = (config ?? {}) as Partial<GateConfig>;
  return {
    blockedTerms: [...(c.blockedTerms ?? [])],
    allowedLinkDomains: [...(c.allowedLinkDomains ?? [])],
    piiPatterns: c.piiPatterns !== false,
    promisePatterns: [...(c.promisePatterns ?? [])],
    promisePhrases: [...(c.promisePhrases ?? [])],
    instructions: c.instructions ?? "",
    useAiJudge: c.useAiJudge !== false,
    overrideRoles: [
      ...(c.overrideRoles ?? ["owner", "admin", "manager", "supervisor"]),
    ],
    shadowOf: c.shadowOf ?? "block",
    authors: c.authors?.length ? [...c.authors] : ["member"],
  };
}

/** "Quando o contato não responde por 48 horas → concluir como Sem resposta". */
export function ruleSentence(
  rule: LifecycleRule,
  outcomeLabel: (code: string) => string,
): string {
  if (rule.when === "no_inbound_for")
    return t(
      rule.then.choose
        ? "policy.sentence.choose"
        : "policy.sentence.no_inbound_for",
      { hours: rule.hours, outcome: outcomeLabel(rule.then.close) },
    );
  if (rule.when === "unanswered_new_for")
    return t("policy.sentence.unanswered_new_for", {
      hours: rule.hours,
      priority: t(`inbox.priority.${rule.then.priority}`),
    });
  return t("policy.sentence.window_expired");
}

/** The review's settings in a line or two, for the list. */
export function gateSummary(cfg: GateConfig): string[] {
  const out: string[] = [
    t("policy.summary.authors", {
      who: GATE_AUTHORS.filter((a) => cfg.authors.includes(a))
        .map((a) => t(`policy.gate.author_short.${a}`))
        .join(", "),
    }),
  ];
  const words = [...cfg.blockedTerms, ...cfg.promisePhrases];
  if (words.length)
    out.push(
      t("policy.summary.blocks", {
        words: words.slice(0, 4).join(", ") + (words.length > 4 ? "…" : ""),
      }),
    );
  const extras: string[] = [];
  if (cfg.piiPatterns) extras.push(t("policy.summary.pii"));
  if (cfg.allowedLinkDomains.length)
    extras.push(
      t("policy.summary.links", { count: cfg.allowedLinkDomains.length }),
    );
  extras.push(
    cfg.useAiJudge
      ? t("policy.summary.judge_on")
      : t("policy.summary.judge_off"),
  );
  out.push(extras.join(" · "));
  return out;
}

/** "toda a empresa", "equipe Vendas". */
export function scopeText(
  p: Pick<PolicyRow, "scope_kind" | "scope_id">,
  teamName: (id: string) => string | null,
  channelName?: (id: string) => string | null,
): string {
  if (p.scope_kind === "team" && p.scope_id)
    return t("policy.scope_team", { name: teamName(p.scope_id) ?? "—" });
  if (p.scope_kind === "channel" && p.scope_id && channelName)
    return t("policy.scope_channel", { name: channelName(p.scope_id) ?? "—" });
  if (p.scope_kind === "channel")
    return t("policy.scope.channel").toLowerCase();
  return t("policy.scope.customer").toLowerCase();
}

/** "48 h", "5 dias": a silence's length as people say it. */
export function hoursText(hours: number): string {
  return hours > 72 && hours % 24 === 0
    ? t("followup.days", { count: hours / 24 })
    : t("followup.hours", { n: hours });
}

/** The lengths a closing rule offers in a sentence. */
export const SILENCE_HOURS = [12, 24, 48, 72, 120, 168] as const;

/** "72 h sem resposta → Sem resposta", "48 h sem resposta → a IA escolhe (ou Sem resposta)". */
export function ruleShort(
  rule: { hours: number; outcome: string; choose: boolean },
  outcomeLabel: (code: string) => string,
): string {
  const outcome = outcomeLabel(rule.outcome);
  return t("policy.short", {
    hours: hoursText(rule.hours),
    outcome: rule.choose ? t("policy.choose", { outcome }) : outcome,
  });
}
